import React from "react";
import { Button } from "@UI/Components/Button/Button";
import { Icon } from "@UI/Components/Icon/Icon";
import { CircleInfo } from "@UI/Components/Icons/CircleInfo";
import { Tooltip } from "@UI/Components/Tooltip/Tooltip";
import { useDisclosure } from "@UI/hooks/useDisclosure";
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";
import useI18n from "@I18n/hooks/useI18n";
import messages from "./messages";

interface ModeratedContentInfoProps {
  /**
   * Where the tooltip portals to. Chakra sends it to the body by default, and
   * Popper then places it in document space — wrong for a caller inside a
   * `position: fixed` overlay over a scrolled page, where the tooltip lands
   * off-screen by the scroll offset. Such a caller passes a container of its
   * own inside that overlay.
   */
  portalContainerRef?: React.RefObject<HTMLElement | null>;
}

export const ModeratedContentInfo: React.FC<ModeratedContentInfoProps> = ({
  portalContainerRef,
}) => {
  const { formatMessage } = useI18n();
  // Same hover+tap handling as InfoTooltip.tsx — Chakra's Tooltip alone
  // can't be triggered reliably on touch — but wrapping the "Add photos"
  // button styling instead of a bare icon, so it can't reuse InfoTooltip directly.
  const { isOpen, onOpen, onClose, onToggle } = useDisclosure();

  return (
    <Tooltip
      hasArrow
      label={formatMessage(messages["moderatedContent.tooltip"])}
      placement="top"
      borderRadius={5}
      py={2}
      px={4}
      bgColor="gray.800"
      portalProps={{ containerRef: portalContainerRef }}
      isOpen={isOpen}
    >
      <Button
        type="button"
        size="sm"
        variant="outline"
        leftIcon={<Icon as={CircleInfo} width="4" height="4" />}
        onMouseEnter={onOpen}
        onMouseLeave={onClose}
        onClick={onToggle}
      >
        <FormattedMessage {...messages["moderatedContent.buttonLabel"]} />
      </Button>
    </Tooltip>
  );
};
